import {Price} from '@aboutyou/grpc-clients/tadarida/aysa_api/models//price.types';
import {
  ArticleDetailPageContext,
  TrackingProductCategory,
  TrackingRecoSectionContext,
  TrackingStreamSectionContext,
  trackAdpView,
} from '@aboutyou/legacy-tracking-sdk';
import {useHistory} from '@aboutyou/router';
import {exists} from '@aboutyou/utils';
import {useEffect} from 'react';
import {useConvertUserAndSessionToCustomer} from '../trackerComponents/TrackCustomer/useConvertUserAndSessionToCustomer';
import {getProduct} from '../utils/getTrackingProduct';
import {trackCustomerDataOnce} from '../utils/trackCustomerDataOnce';
import {getSections} from '../utils/trackingProductGetter';
import {Brand, Product} from '../utils/types';
import {useTracking} from '../utils/utils';
import {useForceUpdateOnBasketFlyoutClose} from './useForceUpdateOnBasketFlyoutClose';

export const useTrackDetailPage = ({
  product,
  price,
  brand,
  pageTitle,
  shouldTrackViewItemEvent,
  section,
  badgesList,
}: {
  product: Product;
  price?: Price;
  brand: Brand;
  pageTitle: string;
  shouldTrackViewItemEvent: boolean;
  section?: TrackingStreamSectionContext['recoType'];
  badgesList?: string[];
}) => {
  const {TrackingClient, pageContextCallback} = useTracking();
  const customer = useConvertUserAndSessionToCustomer();

  // This hook returns a new value whenever the basket flyout is closed,
  // which is used to force an update in the useEffect below, which in turn fires the content_view event for the page beneath the basket flyout.
  const forceUpdateOnBasketFlyoutClose = useForceUpdateOnBasketFlyoutClose();

  const {
    location: {state},
  } = useHistory();

  const customSection =
    state &&
    'customSection' in
      (state as {customSection: TrackingRecoSectionContext['recoType']})
      ? (state as {customSection: TrackingRecoSectionContext['recoType']})
          .customSection
      : undefined;

  const sections = getSections({
    elements: [],
    recoType: customSection ?? section,
    pageType: pageContextCallback().type,
  });

  // eslint-disable-next-line local-rules/too-complex-useeffects -- (rule is filename based --> we should rename this file to match the hooks-name) TODO: Cleanup complex useEffect https://aboutyou.atlassian.net/browse/ASFE-7605
  useEffect(() => {
    const pageContext = pageContextCallback();
    if (product === undefined) {
      return;
    }

    const lastVisitedCategory = window.clientState
      ? window.clientState.getCurrent().lastVisitedCategory
      : undefined;

    const lastVisitedCategoryId = lastVisitedCategory
      ? lastVisitedCategory.id
      : '';
    const lastVisitedCategoryName = lastVisitedCategory
      ? lastVisitedCategory.url?.split('/')[3]
      : '';
    const lastVisitedCategoryPath = lastVisitedCategory
      ? lastVisitedCategory.url
      : '';

    if (shouldTrackViewItemEvent && customer) {
      trackCustomerDataOnce(TrackingClient, customer);
      TrackingClient.track(
        trackAdpView({
          page: {
            ...pageContext,
            title: pageTitle,
          } as ArticleDetailPageContext,
          product: getProduct({
            product,
            price,
            brand,
            badges: badgesList ?? [],
          }),
          productCategories: [
            lastVisitedCategoryId &&
            lastVisitedCategoryName &&
            lastVisitedCategoryPath
              ? {
                  id: lastVisitedCategoryId,
                  name: lastVisitedCategoryName,
                  path: lastVisitedCategoryPath,
                }
              : undefined,
          ].filter<TrackingProductCategory>(exists),
          customElementAndSection:
            customSection || section ? sections : undefined,
        }),
      );
    }
  }, [
    product.id,
    customer?.type,
    shouldTrackViewItemEvent,
    forceUpdateOnBasketFlyoutClose,
  ]);
};
